import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import * as Sentry from '@sentry/react';

// Mapping of property IDs to names for display
const propertyIdToName = {
    40: 'Opera Place',
    10: 'Bridge City Shopping Centre',
    36: 'King Senzangakhona Shopping Centre',
    17: 'Kuyasa Shopping Centre',
    18: 'Maxwell Centre',
    20: 'Motherwell Shopping Centre',
    9: 'Alexandra Plaza',
    14: 'Heidelberg Mall',
    38: 'KG Mall',
    24: 'Setsing IV',
    33: 'Murchison Mall',
    41: 'Boitekong Mall',
    13: 'Gateway Mall',
    26: 'The Crossing',
    12: 'Eyethu Orange Farm',
    15: 'Kamaqhekeza',
    22: 'Nkomazi Plaza',
    39: 'Sam Ntuli Mall',
    25: 'Sontonga Mall',
    11: 'Diepsloot Mall',
    16: 'Kanyamazane Centre',
    19: 'Mkhuhlu Plaza',
    21: 'Moutse Mall',
    27: 'Thulamahashe Plaza'
};

// Returns the most recent Friday ≤ today (inclusive), anchoring the Sat→Fri reporting week
const getWeeklyDateRanges = (now) => {
    const day = now.getDay();              // 0=Sun, 1=Mon ... 5=Fri, 6=Sat
    const daysToFriday = (day + 2) % 7;   // 0 on Fri, 1 on Sat, 2 on Sun, 3 on Mon ...

    const friday = new Date(now);
    friday.setDate(now.getDate() - daysToFriday);

    const currentEnd = new Date(friday);
    const currentStart = new Date(friday); currentStart.setDate(friday.getDate() - 6);
    const prevEnd = new Date(friday); prevEnd.setDate(friday.getDate() - 7);
    const prevStart = new Date(friday); prevStart.setDate(friday.getDate() - 13);

    return { currentStart, currentEnd, prevStart, prevEnd };
};

const RegionalPortfolioStats = ({ period }) => {
    const { apiFetch } = useAuth();
    const [siteVisitData, setSiteVisitData] = useState([]);
    const [prevPeriodSiteVisitData, setPrevPeriodSiteVisitData] = useState([]);
    const [shortPostingsData, setShortPostingsData] = useState([]);
    const [prevPeriodShortPostingsData, setPrevPeriodShortPostingsData] = useState([]);
    const [sipaData, setSipaData] = useState([]);
    const [incidentsData, setIncidentsData] = useState([]);
    const [prevPeriodIncidentsData, setPrevPeriodIncidentsData] = useState([]);
    const [bloodhoundData, setBloodhoundData] = useState([]);
    const [prevPeriodBloodhoundData, setPrevPeriodBloodhoundData] = useState([]);
    const [incompleteChecklistData, setIncompleteChecklistData] = useState({});
    const [prevPeriodChecklistData, setPrevPeriodChecklistData] = useState({});
    const [combinedAssetStatus, setCombinedAssetStatus] = useState(null);
    const [networkDowntimeData, setNetworkDowntimeData] = useState([]);
    const [prevWeekDowntimeData, setPrevWeekDowntimeData] = useState([]);
    
    const [outstandingCertData, setOutstandingCertData] = useState([]);
    const [viewMode, setViewMode] = useState('weekly');
    const [tooltipOpen, setTooltipOpen] = useState(false);

    // Property groups for each person
    const propertyGroups = {
        Etienne: ['Opera Place'],
        Bob: ['Bridge City Shopping Centre', 'King Senzangakhona Shopping Centre', 'Kuyasa Shopping Centre', 'Maxwell Centre', 'Motherwell Shopping Centre'],
        Johan: ['Alexandra Plaza', 'Heidelberg Mall', 'KG Mall', 'Setsing IV', 'Murchison Mall'],
        Marina: ['Boitekong Mall', 'Gateway Mall', 'The Crossing'],
        Simphiwe: ['Eyethu Orange Farm Mall', 'Kamaqhekeza Plaza', 'Nkomazi Plaza', 'Sontonga Mall', 'Sam Ntuli Mall'],
        Jean: ['Diepsloot Mall', 'Kanyamazane Centre', 'Mkhuhlu Plaza', 'Moutse Mall', 'Thulamahashe Plaza'],
    };
    const allProperties = Object.values(propertyGroups).flat();

    // Property IDs per group — parallel to propertyGroups (full names)
    const propertyGroupIds = {
        Etienne: [40],
        Bob: [10, 36, 17, 18, 20],
        Johan: [9, 14, 38, 24, 33],
        Marina: [41, 13, 26],
        Simphiwe: [12, 15, 22, 25, 39],
        Jean: [11, 16, 19, 21, 27],
    };
    const allPropertyIds = Object.values(propertyGroupIds).flat();

    // Property IDs per group using the CapTrac Properties table.
    // These are DIFFERENT from propertyGroupIds (which are Assets DB CentreIDs).
    // Used exclusively for checklist lookups since CMDailyChecklistSubmissions.PropertyID
    // references the CapTrac Properties table, not the Assets DB.
    const checklistPropertyGroupIds = {
        Etienne: [24],
        Bob: [20, 21, 17, 22, 18],
        Johan: [1, 13, 5, 9, 23],
        Marina: [19, 12, 2],
        Simphiwe: [11, 3, 7, 15, 14],
        Jean: [10, 4, 6, 16, 8],
    };

    // #region Fetch Data

    //Fetches Site Visit data for current and prev period, based on viewmode (weekly vs monthly)
    const fetchSiteVisitsData = async () => {
        try {
            const now = new Date();
            const toIso = d =>
                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

            let currentStart, currentEnd, prevStart, prevEnd;

            if (viewMode === 'weekly') {
                ({ currentStart, currentEnd, prevStart, prevEnd } = getWeeklyDateRanges(now));
            } else {
                // Monthly: 1st of current month through today
                currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
                currentEnd = now;
                // Prev period: full previous calendar month
                prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                prevEnd = new Date(now.getFullYear(), now.getMonth(), 0); // last day of prev month
            }

            const [currentRes, prevRes] = await Promise.all([
                apiFetch(`/api/Ops/GetSiteVisitsByDateRange?startDate=${toIso(currentStart)}&endDate=${toIso(currentEnd)}`),
                apiFetch(`/api/Ops/GetSiteVisitsByDateRange?startDate=${toIso(prevStart)}&endDate=${toIso(prevEnd)}`)
            ]);

            const currentData = await currentRes.json();
            const prevData = await prevRes.json();

            if (currentData.success && currentData.data) setSiteVisitData(currentData.data);
            if (prevData.success && prevData.data) setPrevPeriodSiteVisitData(prevData.data);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching site visits data:', e);
        }
    };

    const fetchShortPostingsData = async () => {
        try {
            const now = new Date();
            const toIso = d =>
                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

            let currentStart, currentEnd, prevStart, prevEnd;

            if (viewMode === 'weekly') {
                currentEnd = new Date(now); currentEnd.setDate(now.getDate() - 1);
                currentStart = new Date(now); currentStart.setDate(now.getDate() - 7);
                prevEnd = new Date(now); prevEnd.setDate(now.getDate() - 8);
                prevStart = new Date(now); prevStart.setDate(now.getDate() - 14);
            } else {
                currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
                currentEnd = now;
                prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);
            }

            const [currentRes, prevRes] = await Promise.all([
                apiFetch(`/api/Ops/GetShortPostingsByDateRange?startDate=${toIso(currentStart)}&endDate=${toIso(currentEnd)}`),
                apiFetch(`/api/Ops/GetShortPostingsByDateRange?startDate=${toIso(prevStart)}&endDate=${toIso(prevEnd)}`)
            ]);

            const currentData = await currentRes.json();
            const prevData = await prevRes.json();

            if (currentData.success && currentData.data) setShortPostingsData(currentData.data);
            if (prevData.success && prevData.data) setPrevPeriodShortPostingsData(prevData.data);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching short postings data:', e);
        }
    };

    //Function for fetching SIPA data from the API
    const fetchSipaData = async () => {
        try {
            const now = new Date();
            const currentPeriod = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
            const response = await apiFetch(`/api/Ops/GetSipaReportScores?period=${currentPeriod}`);
            const data = await response.json();
            if (data.success && data.data) {
                setSipaData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching SIPA data:', e);
        }
    };

    const fetchIncidentsData = async () => {
        try {
            const now = new Date();
            const toIso = d =>
                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

            let currentStart, currentEnd, prevStart, prevEnd;

            if (viewMode === 'weekly') {
                currentEnd = new Date(now); currentEnd.setDate(now.getDate() - 1);
                currentStart = new Date(now); currentStart.setDate(now.getDate() - 7);
                prevEnd = new Date(now); prevEnd.setDate(now.getDate() - 8);
                prevStart = new Date(now); prevStart.setDate(now.getDate() - 14);
            } else {
                currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
                currentEnd = now;
                prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);
            }

            const [currentRes, prevRes] = await Promise.all([
                apiFetch(`/api/Ops/GetIncidentsByDateRange?startDate=${toIso(currentStart)}&endDate=${toIso(currentEnd)}`),
                apiFetch(`/api/Ops/GetIncidentsByDateRange?startDate=${toIso(prevStart)}&endDate=${toIso(prevEnd)}`)
            ]);

            const currentData = await currentRes.json();
            const prevData = await prevRes.json();

            if (currentData.success && currentData.data) setIncidentsData(currentData.data);
            if (prevData.success && prevData.data) setPrevPeriodIncidentsData(prevData.data);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching incidents data:', e);
        }
    };

    const fetchBloodhoundData = async () => {
        try {
            const now = new Date();
            const toIso = d =>
                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

            let currentStart, currentEnd, prevStart, prevEnd;

            if (viewMode === 'weekly') {
                currentEnd = new Date(now); currentEnd.setDate(now.getDate() - 1);
                currentStart = new Date(now); currentStart.setDate(now.getDate() - 7);
                prevEnd = new Date(now); prevEnd.setDate(now.getDate() - 8);
                prevStart = new Date(now); prevStart.setDate(now.getDate() - 14);
            } else {
                currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
                currentEnd = now;
                prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);
            }

            const [currentRes, prevRes] = await Promise.all([
                apiFetch(`/api/Ops/GetBloodhoundScoresByDateRange?startDate=${toIso(currentStart)}&endDate=${toIso(currentEnd)}`),
                apiFetch(`/api/Ops/GetBloodhoundScoresByDateRange?startDate=${toIso(prevStart)}&endDate=${toIso(prevEnd)}`)
            ]);

            const currentData = await currentRes.json();
            const prevData = await prevRes.json();

            if (currentData.success && currentData.data) setBloodhoundData(currentData.data);
            if (prevData.success && prevData.data) setPrevPeriodBloodhoundData(prevData.data);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching bloodhound data:', e);
        }
    };

    const fetchOutstandingCertificateData = async () => {
        try {
            const response = await apiFetch('/api/Ops/GetOutstandingCertificates');
            const data = await response.json();
            if (data.success && data.data) {
                setOutstandingCertData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching outstanding certificate data:', e);
        }
    };

    const fetchCombinedAssetStatus = async () => {
        try {
            const response = await apiFetch('/api/Ops/GetCombinedAssetServiceStatus');
            const data = await response.json();
            if (data.success && data.data) {
                setCombinedAssetStatus(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching combined asset status:', e);
        }
    };

    const fetchNetworkDowntime = async () => {
        try {
            const now = new Date();
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const toIso = d =>
                d.getFullYear() + '-' +
                String(d.getMonth() + 1).padStart(2, '0') + '-' +
                String(d.getDate()).padStart(2, '0');

            const response = await apiFetch(
                `/api/RouterConfig/mall-outage-events?startDate=${toIso(monthStart)}&endDate=${toIso(now)}`
            );
            if (!response.ok) return;
            const data = await response.json();

            // Accumulate intervals per Zabbix site name
            const siteIntervals = {};
            (data?.events || []).forEach(p => {
                if (!p.clock || !p.duration_seconds) return;
                const startMs = parseInt(p.clock) * 1000;
                const endMs = startMs + p.duration_seconds * 1000;
                const siteNames = Array.isArray(p.hosts) ? p.hosts.map(h => h.name).filter(Boolean) : [];
                siteNames.forEach(site => {
                    if (!siteIntervals[site]) siteIntervals[site] = [];
                    siteIntervals[site].push([startMs, endMs]);
                });
            });

            // Merge overlapping intervals and convert to hours
            const mergeToHours = (intervals) => {
                if (!intervals?.length) return 0;
                const sorted = intervals.slice().sort((a, b) => a[0] - b[0]);
                let total = 0, [cs, ce] = sorted[0];
                for (let i = 1; i < sorted.length; i++) {
                    const [s, e] = sorted[i];
                    if (s <= ce) ce = Math.max(ce, e); else { total += ce - cs; cs = s; ce = e; }
                }
                return (total + ce - cs) / 3600000;
            };

            // Build a lookup of lowercased property names to IDs for matching
            const propertyEntries = Object.entries(propertyIdToName).map(([id, name]) => ({
                id: parseInt(id),
                lowerName: name.toLowerCase()
            }));

            // Match a Zabbix site name to a property ID using two rules:
            // Rule 1: site name starts with the full property name (e.g. "KG Mall HAP" → "KG Mall")
            // Rule 2: property name starts with the first N words of the site name (e.g. "Bridge City Control Room" → "Bridge City")
            const findPropertyId = (siteName) => {
                const lowerSite = siteName.toLowerCase().trim();
                const siteWords = lowerSite.split(/\s+/);

                // Rule 1 — prefer the longest matching property name prefix
                let best = null;
                let bestLen = 0;
                for (const prop of propertyEntries) {
                    if (lowerSite.startsWith(prop.lowerName) && prop.lowerName.length > bestLen) {
                        bestLen = prop.lowerName.length;
                        best = prop.id;
                    }
                }
                if (best) return best;

                // Rule 2 — try matching decreasing word counts from the site name against the start of property names
                for (let wordCount = Math.min(siteWords.length, 3); wordCount >= 1; wordCount--) {
                    const prefix = siteWords.slice(0, wordCount).join(' ');
                    const matches = propertyEntries.filter(p => p.lowerName.startsWith(prefix));
                    if (matches.length === 1) return matches[0].id;
                    if (matches.length > 1) {
                        // Multiple candidates — pick the one with the longest name (most specific)
                        return matches.sort((a, b) => b.lowerName.length - a.lowerName.length)[0].id;
                    }
                }
                return null;
            };

            // Aggregate merged downtime hours per property ID
            // (multiple Zabbix sites, e.g. "Bridge City Control Room" + "Bridge City CM Office", map to the same property)
            const propertyDowntime = {};
            Object.keys(siteIntervals).forEach(site => {
                const propertyId = findPropertyId(site);
                if (!propertyId) return;
                const hours = mergeToHours(siteIntervals[site]);
                propertyDowntime[propertyId] = (propertyDowntime[propertyId] ?? 0) + hours;
            });

            const result = Object.entries(propertyDowntime).map(([id, downtimeHours]) => ({
                propertyId: parseInt(id),
                downtimeHours
            }));

            setNetworkDowntimeData(result);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching network downtime:', e);
        }
    };

    const fetchChecklistsData = async () => {
        try {
            const now = new Date();
            const toIso = d =>
                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

            let currentStart, currentEnd, prevStart, prevEnd;

            if (viewMode === 'weekly') {
                currentEnd = new Date(now); currentEnd.setDate(now.getDate() - 1);
                currentStart = new Date(now); currentStart.setDate(now.getDate() - 7);
                prevEnd = new Date(now); prevEnd.setDate(now.getDate() - 8);
                prevStart = new Date(now); prevStart.setDate(now.getDate() - 14);
            } else {
                currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
                currentEnd = now;
                prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);
            }

            const [currentRes, prevRes] = await Promise.all([
                apiFetch(`/api/Ops/GetChecklistMissedDaysByDateRange?startDate=${toIso(currentStart)}&endDate=${toIso(currentEnd)}`),
                apiFetch(`/api/Ops/GetChecklistMissedDaysByDateRange?startDate=${toIso(prevStart)}&endDate=${toIso(prevEnd)}`)
            ]);

            const currentData = await currentRes.json();
            const prevData = await prevRes.json();

            if (currentData.success && currentData.data) setIncompleteChecklistData(currentData.data);
            if (prevData.success && prevData.data) setPrevPeriodChecklistData(prevData.data);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error fetching checklists data:', e);
        }
    };

    // Fetch data when the component mounts or when the period changes
    useEffect(() => {
        if (!period) return;
        const timer = setTimeout(() => {
            fetchSipaData();
            fetchIncidentsData();
            fetchCombinedAssetStatus();
            fetchChecklistsData();
            fetchNetworkDowntime();
            fetchBloodhoundData();
            fetchSiteVisitsData();
            fetchShortPostingsData();
            fetchOutstandingCertificateData();
        }, 0);
        return () => clearTimeout(timer);
    }, [period, viewMode]);

    // #endregion

    // #region Site Visits Data Processing

    // Count site visits for a group from any source dataset (defaults to current period)
    const getSiteVisitCount = (propertyNames, source = siteVisitData) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        return source.filter(v => names.includes(v.propertyName)).length;
    };

    const siteVisitsEtienne = getSiteVisitCount(propertyGroups.Etienne);
    const siteVisitsBob = getSiteVisitCount(propertyGroups.Bob);
    const siteVisitsJohan = getSiteVisitCount(propertyGroups.Johan);
    const siteVisitsMarina = getSiteVisitCount(propertyGroups.Marina);
    const siteVisitsSimphiwe = getSiteVisitCount(propertyGroups.Simphiwe);
    const siteVisitsJean = getSiteVisitCount(propertyGroups.Jean);
    const siteVisitsTotal = getSiteVisitCount(allProperties);

    const prevPeriodSiteVisitsTotal = getSiteVisitCount(allProperties, prevPeriodSiteVisitData);
    const siteVisitsVariance = siteVisitsTotal - prevPeriodSiteVisitsTotal;

    const renderSiteVisitTooltipTable = (propertyNames, source = siteVisitData) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        const details = names.map(name => ({
            propertyName: name,
            count: source.filter(v => v.propertyName === name).length
        })).filter(d => d.count > 0);

        if (!details.length) return '<div>No site visits</div>';
        return `
        <table style="border-collapse:collapse; table-layout:fixed; width:260px;">
            <thead>
                <tr>
                    <th style="padding:2px 8px; width:210px; text-align:left;">Property</th>
                    <th style="padding:2px 8px; width:50px; text-align:right;">Visits</th>
                </tr>
            </thead>
            <tbody>
                ${details.map(d => `
                    <tr>
                        <td style="padding:2px 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${d.propertyName}">${d.propertyName}</td>
                        <td style="padding:2px 8px; text-align:right;">${d.count}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    };
    // #endregion

    // #region SIPA Data Processing

    // Returns the average sipaScore for one or more propertyIDs, capped at 2 decimal places
    const getAverageSipaScore = (propertyNames) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        const filtered = sipaData.filter(item => names.includes(item.propertyName));
        if (filtered.length === 0) return null;
        const scores = filtered.map(item => {
            const ga = item.scoreGeneralAppearance ?? null;
            const oc = item.scoreOperationsCompliance ?? null;
            if (ga !== null && oc !== null) return (ga + oc) / 2;
            return ga ?? oc ?? null;
        }).filter(s => s !== null);
        if (scores.length === 0) return null;
        return Math.round((scores.reduce((sum, s) => sum + s, 0) / scores.length) * 100) / 100;
    };

    const getSipaDetailsForGroup = (propertyNames) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        return sipaData
            .filter(item => names.includes(item.propertyName))
            .map(item => {
                const ga = item.scoreGeneralAppearance ?? null;
                const oc = item.scoreOperationsCompliance ?? null;
                let sipaScore = null;
                if (ga !== null && oc !== null) sipaScore = Math.round(((ga + oc) / 2) * 100) / 100;
                else sipaScore = Math.round((ga ?? oc ?? 0) * 100) / 100;
                return {
                    propertyName: item.propertyName,
                    sipaScore
                };
            });
    };

    const avgSipaEtienne = getAverageSipaScore(propertyGroups.Etienne);
    const avgSipaBob = getAverageSipaScore(propertyGroups.Bob);
    const avgSipaJohan = getAverageSipaScore(propertyGroups.Johan);
    const avgSipaMarina = getAverageSipaScore(propertyGroups.Marina);
    const avgSipaSimphiwe = getAverageSipaScore(propertyGroups.Simphiwe);
    const avgSipaJean = getAverageSipaScore(propertyGroups.Jean);
    const avgSipaTotal = getAverageSipaScore(allProperties);

    // Function to render the SIPA tooltip table
    const renderSipaTooltipTable = (propertyIDs) => {
        const details = getSipaDetailsForGroup(propertyIDs);
        if (!details.length) return '<div>No data</div>';
        return `
        <table style="border-collapse:collapse; table-layout:fixed; width:260px;">
            <thead>
                <tr>
                    <th style="padding:2px 8px; width:190px; text-align:left;">Property</th>
                    <th style="padding:2px 8px; width:70px; text-align:right;">SIPA Score</th>
                </tr>
            </thead>
            <tbody>
                ${details.map(d => `
                    <tr>
                        <td style="padding:2px 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${d.propertyName}">${d.propertyName}</td>
                        <td style="padding:2px 8px; text-align:right;">${d.sipaScore}%</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    };

    // #endregion

    // #region Short Postings Data Processing

    const getShortPostingCount = (propertyNames, source = shortPostingsData) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        return source.filter(v => names.includes(v.propertyName)).length;
    };

    const shortPostingsEtienne = getShortPostingCount(propertyGroups.Etienne);
    const shortPostingsBob = getShortPostingCount(propertyGroups.Bob);
    const shortPostingsJohan = getShortPostingCount(propertyGroups.Johan);
    const shortPostingsMarina = getShortPostingCount(propertyGroups.Marina);
    const shortPostingsSimphiwe = getShortPostingCount(propertyGroups.Simphiwe);
    const shortPostingsJean = getShortPostingCount(propertyGroups.Jean);
    const shortPostingsTotal = getShortPostingCount(allProperties);

    const prevPeriodShortPostingsTotal = getShortPostingCount(allProperties, prevPeriodShortPostingsData);
    const shortPostingsVariance = shortPostingsTotal - prevPeriodShortPostingsTotal;

    const renderShortPostingTooltipTable = (propertyNames, source = shortPostingsData) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        const details = names.map(name => ({
            propertyName: name,
            count: source.filter(v => v.propertyName === name).length
        })).filter(d => d.count > 0);

        if (!details.length) return '<div>No short postings</div>';
        return `
        <table style="border-collapse:collapse; table-layout:fixed; width:260px;">
            <thead>
                <tr>
                    <th style="padding:2px 8px; width:210px; text-align:left;">Property</th>
                    <th style="padding:2px 8px; width:50px; text-align:right;">Count</th>
                </tr>
            </thead>
            <tbody>
                ${details.map(d => `
                    <tr>
                        <td style="padding:2px 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${d.propertyName}">${d.propertyName}</td>
                        <td style="padding:2px 8px; text-align:right;">${d.count}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    };

    // #endregion

    // #region Incidents Data Processing

    // Generic function to get total for any incident type from any source dataset
    const getTotalIncident = (incidentType, propertyNames, source = incidentsData) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        return source
            .filter(item => names.includes(item.propertyName))
            .reduce((sum, item) => sum + (item[incidentType] || 0), 0);
    };

    const armedRobEtienne = getTotalIncident('armedRobbery', propertyGroups.Etienne);
    const armedRobBob = getTotalIncident('armedRobbery', propertyGroups.Bob);
    const armedRobJohan = getTotalIncident('armedRobbery', propertyGroups.Johan);
    const armedRobMarina = getTotalIncident('armedRobbery', propertyGroups.Marina);
    const armedRobSimphiwe = getTotalIncident('armedRobbery', propertyGroups.Simphiwe);
    const armedRobJean = getTotalIncident('armedRobbery', propertyGroups.Jean);
    const armedRobTotal = getTotalIncident('armedRobbery', allProperties);

    const breakInEtienne = getTotalIncident('breakIn', propertyGroups.Etienne);
    const breakInBob = getTotalIncident('breakIn', propertyGroups.Bob);
    const breakInJohan = getTotalIncident('breakIn', propertyGroups.Johan);
    const breakInMarina = getTotalIncident('breakIn', propertyGroups.Marina);
    const breakInSimphiwe = getTotalIncident('breakIn', propertyGroups.Simphiwe);
    const breakInJean = getTotalIncident('breakIn', propertyGroups.Jean);
    const breakInTotal = getTotalIncident('breakIn', allProperties);

    const assaultEtienne = getTotalIncident('assaultOrInjury', propertyGroups.Etienne);
    const assaultBob = getTotalIncident('assaultOrInjury', propertyGroups.Bob);
    const assaultJohan = getTotalIncident('assaultOrInjury', propertyGroups.Johan);
    const assaultMarina = getTotalIncident('assaultOrInjury', propertyGroups.Marina);
    const assaultSimphiwe = getTotalIncident('assaultOrInjury', propertyGroups.Simphiwe);
    const assaultJean = getTotalIncident('assaultOrInjury', propertyGroups.Jean);
    const assaultTotal = getTotalIncident('assaultOrInjury', allProperties);

    const damageEtienne = getTotalIncident('damageToProperty', propertyGroups.Etienne);
    const damageBob = getTotalIncident('damageToProperty', propertyGroups.Bob);
    const damageJohan = getTotalIncident('damageToProperty', propertyGroups.Johan);
    const damageMarina = getTotalIncident('damageToProperty', propertyGroups.Marina);
    const damageSimphiwe = getTotalIncident('damageToProperty', propertyGroups.Simphiwe);
    const damageJean = getTotalIncident('damageToProperty', propertyGroups.Jean);
    const damageTotal = getTotalIncident('damageToProperty', allProperties);

    // Previous period totals (portfolio level) for variance
    const prevPeriodArmedRob = getTotalIncident('armedRobbery', allProperties, prevPeriodIncidentsData);
    const prevPeriodBreakIn = getTotalIncident('breakIn', allProperties, prevPeriodIncidentsData);
    const prevPeriodAssault = getTotalIncident('assaultOrInjury', allProperties, prevPeriodIncidentsData);
    const prevPeriodDamage = getTotalIncident('damageToProperty', allProperties, prevPeriodIncidentsData);
    const prevPeriodDeath = getTotalIncident('deathOnSite', allProperties, prevPeriodIncidentsData);

    const renderIncidentTooltipTable = (incidentType, propertyNames, source = incidentsData) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        const propertyIncidentMap = {};
        source
            .filter(item => names.includes(item.propertyName))
            .forEach(item => {
                const name = item.propertyName;
                const count = item[incidentType] || 0;
                if (!propertyIncidentMap[name]) propertyIncidentMap[name] = 0;
                propertyIncidentMap[name] += count;
            });

        const details = Object.entries(propertyIncidentMap)
            .filter(([, count]) => count > 0)
            .map(([propertyName, count]) => ({ propertyName, count }));

        if (!details.length) return '<div>No incidents</div>';
        return `
        <table style="border-collapse:collapse; table-layout:fixed; width:260px;">
            <thead>
                <tr>
                    <th style="padding:2px 8px; width:210px; text-align:left;">Property</th>
                    <th style="padding:2px 8px; width:50px; text-align:right;">Count</th>
                </tr>
            </thead>
            <tbody>
                ${details.map(d => `
                    <tr>
                        <td style="padding:2px 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${d.propertyName}">${d.propertyName}</td>
                        <td style="padding:2px 8px; text-align:right;">${d.count}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    };

    // #endregion

    // #region Bloodhound Data Processing

    // Average score helper — works on any bloodhound source slice
    const getAverageBloodhoundScore = (propertyNames, sourceData = bloodhoundData) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        const filtered = sourceData.filter(item => names.includes(item.propertyName));
        if (filtered.length === 0) return null;
        const total = filtered.reduce((sum, item) => sum + (item.score || 0), 0);
        return Math.round((total / filtered.length) * 100) / 100;
    };

    const avgBloodhoundEtienne = getAverageBloodhoundScore(propertyGroups.Etienne);
    const avgBloodhoundBob = getAverageBloodhoundScore(propertyGroups.Bob);
    const avgBloodhoundJohan = getAverageBloodhoundScore(propertyGroups.Johan);
    const avgBloodhoundMarina = getAverageBloodhoundScore(propertyGroups.Marina);
    const avgBloodhoundSimphiwe = getAverageBloodhoundScore(propertyGroups.Simphiwe);
    const avgBloodhoundJean = getAverageBloodhoundScore(propertyGroups.Jean);
    const avgBloodhoundTotal = getAverageBloodhoundScore(allProperties);

    const avgBloodhoundPrevPeriod = getAverageBloodhoundScore(allProperties, prevPeriodBloodhoundData);
    const bloodhoundVariance = (avgBloodhoundTotal !== null && avgBloodhoundPrevPeriod !== null)
        ? Math.round((avgBloodhoundTotal - avgBloodhoundPrevPeriod) * 100) / 100
        : null;

    const renderBloodhoundTooltipTable = (propertyNames, sourceData = bloodhoundData) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];

        // Group by property and compute average score (mirrors getAverageBloodhoundScore per-property)
        const scoreMap = {};
        sourceData
            .filter(item => names.includes(item.propertyName))
            .forEach(item => {
                const name = item.propertyName;
                const s = item.score ?? 0;
                if (!scoreMap[name]) scoreMap[name] = { total: 0, count: 0 };
                scoreMap[name].total += s;
                scoreMap[name].count += 1;
            });

        const details = Object.entries(scoreMap).map(([property, { total, count }]) => ({
            property,
            score: Math.round((total / count) * 100) / 100
        }));

        if (!details.length) return '<div>No data</div>';

        return `
        <table style="border-collapse:collapse; table-layout:fixed; width:260px;">
            <thead>
                <tr>
                    <th style="padding:2px 8px; width:190px; text-align:left;">Property</th>
                    <th style="padding:2px 8px; width:70px; text-align:right;">Score</th>
                </tr>
            </thead>
            <tbody>
                ${details.map(d => `
                    <tr>
                        <td style="padding:2px 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${d.property}">${d.property}</td>
                        <td style="padding:2px 8px; text-align:right;">${d.score}%</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    };

    // #endregion

    // #region Certificates Data Processing

    const getCertOutstandingCount = (propertyNames, certKey) => {
        if (!outstandingCertData.length) return null;
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        return outstandingCertData
            .filter(x => names.includes(x.propertyName))
            .reduce((sum, x) => sum + (x[certKey] || 0), 0);
    };

    const outstandingFire = {
        Etienne: getCertOutstandingCount(propertyGroups.Etienne, 'outstandingFireClearanceCertificateCount'),
        Bob: getCertOutstandingCount(propertyGroups.Bob, 'outstandingFireClearanceCertificateCount'),
        Johan: getCertOutstandingCount(propertyGroups.Johan, 'outstandingFireClearanceCertificateCount'),
        Marina: getCertOutstandingCount(propertyGroups.Marina, 'outstandingFireClearanceCertificateCount'),
        Simphiwe: getCertOutstandingCount(propertyGroups.Simphiwe, 'outstandingFireClearanceCertificateCount'),
        Jean: getCertOutstandingCount(propertyGroups.Jean, 'outstandingFireClearanceCertificateCount'),
        Portfolio: getCertOutstandingCount(allProperties, 'outstandingFireClearanceCertificateCount'),
    };

    const outstandingOccupancy = {
        Etienne: getCertOutstandingCount(propertyGroups.Etienne, 'outstandingOccupancyCertificateCount'),
        Bob: getCertOutstandingCount(propertyGroups.Bob, 'outstandingOccupancyCertificateCount'),
        Johan: getCertOutstandingCount(propertyGroups.Johan, 'outstandingOccupancyCertificateCount'),
        Marina: getCertOutstandingCount(propertyGroups.Marina, 'outstandingOccupancyCertificateCount'),
        Simphiwe: getCertOutstandingCount(propertyGroups.Simphiwe, 'outstandingOccupancyCertificateCount'),
        Jean: getCertOutstandingCount(propertyGroups.Jean, 'outstandingOccupancyCertificateCount'),
        Portfolio: getCertOutstandingCount(allProperties, 'outstandingOccupancyCertificateCount'),
    };

    const renderOutstandingCertTooltip = (propertyNames, certKey) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        const outstanding = outstandingCertData
            .filter(x => names.includes(x.propertyName) && (x[certKey] || 0) > 0)
            .map(x => ({ name: x.propertyName, count: x[certKey] }));

        if (!outstanding.length) return '<div>No outstanding certificates</div>';

        return `
        <table style="border-collapse:collapse; table-layout:fixed; width:300px;">
            <thead>
                <tr>
                    <th style="padding:2px 8px; width:220px; text-align:left;">Property</th>
                    <th style="padding:2px 8px; width:80px; text-align:right;">Outstanding</th>
                </tr>
            </thead>
            <tbody>
                ${outstanding.map(({ name, count }) => `
                    <tr>
                        <td style="padding:2px 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${name}">${name}</td>
                        <td style="padding:2px 8px; text-align:right;">${count}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    };

    // #endregion

    // #region Network Downtime Data Processing

    // Returns downtime in hours for a set of property IDs from internal router monitoring data
    const getNetworkDowntimeHours = (data, propertyIds) => {
        const ids = Array.isArray(propertyIds) ? propertyIds : [propertyIds];
        const total = data
            .filter(d => ids.includes(d.propertyId))
            .reduce((sum, d) => sum + d.downtimeHours, 0);
        return +total.toFixed(2);
    };

    // Returns downtime in hours for the entire portfolio (all properties)
    const getPortfolioNetworkDowntimeHours = (data) => {
        const total = data.reduce((sum, d) => sum + d.downtimeHours, 0);
        return +total.toFixed(2);
    };

    const renderNetworkDowntimeTooltip = (data, propertyIds) => {
        const ids = Array.isArray(propertyIds) ? propertyIds : [propertyIds];
        const rows = ids
            .map(id => {
                const entry = data.find(d => d.propertyId === id);
                const name = propertyIdToName[id] ?? `ID ${id}`;
                return entry && entry.downtimeHours > 0
                    ? `<tr><td style="padding:2px 8px;">${name}</td><td style="padding:2px 8px; text-align:right;">${entry.downtimeHours.toFixed(2)}h</td></tr>`
                    : null;
            })
            .filter(Boolean)
            .join('');
        return rows
            ? `<table style="border-collapse:collapse; table-layout:fixed; width:260px;"><thead><tr><th style="padding:2px 8px; width:210px; text-align:left;">Property</th><th style="padding:2px 8px; width:50px; text-align:right;">Downtime</th></tr></thead><tbody>${rows}</tbody></table>`
            : '<div>No downtime events</div>';
    };

    const downtimeEtienne = getNetworkDowntimeHours(networkDowntimeData, propertyGroupIds.Etienne);
    const downtimeBob = getNetworkDowntimeHours(networkDowntimeData, propertyGroupIds.Bob);
    const downtimeJohan = getNetworkDowntimeHours(networkDowntimeData, propertyGroupIds.Johan);
    const downtimeMarina = getNetworkDowntimeHours(networkDowntimeData, propertyGroupIds.Marina);
    const downtimeSimphiwe = getNetworkDowntimeHours(networkDowntimeData, propertyGroupIds.Simphiwe);
    const downtimeJean = getNetworkDowntimeHours(networkDowntimeData, propertyGroupIds.Jean);

    // Portfolio (all properties)
    const downtimePortfolio = getPortfolioNetworkDowntimeHours(networkDowntimeData);

    // Previous week
    const downtimePrevWeek = getPortfolioNetworkDowntimeHours(prevWeekDowntimeData);

    // Variance (current portfolio - previous week)
    const downtimeVariance = downtimePrevWeek - downtimePortfolio;

    // #endregion

    // #region Checklist Data Processing

    // Create a full-name → Properties table ID map for checklist lookups
    const fullPropertyNameToId = {};
    Object.keys(propertyGroups).forEach(group => {
        propertyGroups[group].forEach((name, i) => {
            fullPropertyNameToId[name] = checklistPropertyGroupIds[group][i];
        });
    });

    const getIncompleteChecklistTotal = (propertyNames, data = incompleteChecklistData) => {
        if (!data || Object.keys(data).length === 0) return 0;
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        let total = 0;
        names.forEach(propertyName => {
            const propertyId = fullPropertyNameToId[propertyName];
            if (propertyId && data[propertyId]) {
                total += data[propertyId];
            }
        });
        return total;
    };

    const checklistsEtienne = getIncompleteChecklistTotal(propertyGroups.Etienne);
    const checklistsBob = getIncompleteChecklistTotal(propertyGroups.Bob);
    const checklistsJohan = getIncompleteChecklistTotal(propertyGroups.Johan);
    const checklistsMarina = getIncompleteChecklistTotal(propertyGroups.Marina);
    const checklistsSimphiwe = getIncompleteChecklistTotal(propertyGroups.Simphiwe);
    const checklistsJean = getIncompleteChecklistTotal(propertyGroups.Jean);
    const checklistsTotal = getIncompleteChecklistTotal(allProperties);
    const prevChecklistsTotal = getIncompleteChecklistTotal(allProperties, prevPeriodChecklistData);
    const varianceTotal = checklistsTotal - prevChecklistsTotal;

    const renderChecklistTooltipTable = (propertyNames, data = incompleteChecklistData) => {
        if (!data || Object.keys(data).length === 0) return '<div>No data</div>';
        const names = Array.isArray(propertyNames) ? propertyNames : [propertyNames];
        const details = names
            .map(propertyName => {
                const propertyId = fullPropertyNameToId[propertyName];
                const count = propertyId ? (data[propertyId] ?? 0) : 0;
                return { propertyName, count };
            })
            .filter(d => d.count > 0);

        if (!details.length) return '<div>No incomplete checklists</div>';

        return `
        <table style="border-collapse:collapse; table-layout:fixed; width:260px;">
            <thead>
                <tr>
                    <th style="padding:2px 8px; width:210px; text-align:left;">Property</th>
                    <th style="padding:2px 8px; width:50px; text-align:right;">Days</th>
                </tr>
            </thead>
            <tbody>
                ${details.map(d => `
                    <tr>
                        <td style="padding:2px 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${d.propertyName}">${d.propertyName}</td>
                        <td style="padding:2px 8px; text-align:right;">${d.count}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    };

    // #endregion

    // Group combined asset data by property manager
    const groupAssetStatus = {};
    if (combinedAssetStatus && combinedAssetStatus.byCentre) {
        Object.keys(propertyGroupIds).forEach((groupName) => {
            const ids = propertyGroupIds[groupName];
            const groupData = combinedAssetStatus.byCentre.filter(x => ids.includes(x.centreID));
            groupAssetStatus[groupName] = {
                due: groupData.reduce((s, x) => s + x.due, 0),
                overdue: groupData.reduce((s, x) => s + x.overdue, 0),
                underMaintenance: groupData.reduce((s, x) => s + x.underMaintenance, 0),
                pendingVerification: groupData.reduce((s, x) => s + x.pendingVerification, 0),
                previousDue: groupData.reduce((s, x) => s + x.previousDue, 0),
                previousOverdue: groupData.reduce((s, x) => s + x.previousOverdue, 0),
                details: groupData
            };
        });
    }

    const portfolioDue = combinedAssetStatus?.totals.due ?? 0;
    const portfolioOverdue = combinedAssetStatus?.totals.overdue ?? 0;
    const portfolioUnderMaintenance = combinedAssetStatus?.totals.underMaintenance ?? 0;
    const portfolioPendingVerification = combinedAssetStatus?.totals.pendingVerification ?? 0;
    const portfolioPreviousDue = combinedAssetStatus?.totals.previousDue ?? 0;
    const portfolioPreviousOverdue = combinedAssetStatus?.totals.previousOverdue ?? 0;
    const portfolioVarianceDue = portfolioPreviousDue - portfolioDue;
    const portfolioVarianceOverdue = portfolioPreviousOverdue - portfolioOverdue;

    const renderAssetTooltipTable = (groupName, statKey) => {
        // For portfolio total, pass null groupName and use all byCentre data
        const details = groupName
            ? (groupAssetStatus[groupName]?.details ?? [])
            : (combinedAssetStatus?.byCentre ?? []);

        const rows = details
            .filter(x => (x[statKey] ?? 0) > 0)
            .map(x => ({ propertyName: propertyIdToName[x.centreID] ?? `ID ${x.centreID}`, count: x[statKey] }));

        if (!rows.length) return '<div>No data</div>';

        return `
        <table style="border-collapse:collapse; table-layout:fixed; width:260px;">
            <thead>
                <tr>
                    <th style="padding:2px 8px; width:210px; text-align:left;">Property</th>
                    <th style="padding:2px 8px; width:50px; text-align:right;">Count</th>
                </tr>
            </thead>
            <tbody>
                ${rows.map(r => `
                    <tr>
                        <td style="padding:2px 8px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${r.propertyName}">${r.propertyName}</td>
                        <td style="padding:2px 8px; text-align:right;">${r.count}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    };

    // #region Render

    return (
        <div onMouseLeave={() => setTooltipOpen(false)}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <label style={{ fontWeight: 'bold', fontSize: '1rem', whiteSpace: 'nowrap' }}>View Mode:</label>
                    <select
                        value={viewMode}
                        onChange={e => setViewMode(e.target.value)}
                        style={{ padding: '2px 6px', borderRadius: '4px', border: '1px solid #ccc', cursor: 'pointer', fontSize: '1rem', width: 'auto' }}
                    >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                    </select>
                </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table cellPadding="6">
                    <thead>
                        <tr>
                            <th colSpan="2" style={{ background: '#F63040', color: '#fff', textAlign: 'center' }}>
                                Regional Portfolio Statistics
                            </th>
                            <th rowSpan="2">Period</th>
                            <th rowSpan="2">Western Cape<br /><span style={{ fontWeight: 400 }}>Etienne Hairbottle</span></th>
                            <th rowSpan="2">Eastern Cape KZN<br /><span style={{ fontWeight: 400 }}>Bob Harcus</span></th>
                            <th rowSpan="2">Gauteng<br />Free State KZN Mpumalanga<br /><span style={{ fontWeight: 400 }}>Johan Rossouw</span></th>
                            <th rowSpan="2">Gauteng<br /><span style={{ whiteSpace: 'nowrap' }}>North West</span><br /><span style={{ fontWeight: 400 }}>Marina VdV</span></th>
                            <th rowSpan="2">Gauteng Mpumalanga<br /><span style={{ fontWeight: 400 }}>Simphiwe Mkutshulwa</span></th>
                            <th rowSpan="2">Gauteng Mpumalanga<br /><span style={{ fontWeight: 400 }}>Jean Botha</span></th>
                            <th rowSpan="2">Portfolio</th>
                            <th rowSpan="2">{viewMode === 'weekly' ? 'Previous Week' : 'Previous Month'}</th>
                            <th rowSpan="2">Variance</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ background: '#E5E4E2' }}>
                            <td rowSpan="2" style={{ background: '#57565B', color: '#fff', fontWeight: 'bold' }}>Centres</td>
                            <td style={{ textAlign: 'left', fontWeight: 'bold' }}>Site visits</td>
                            <td>{viewMode === 'weekly' ? 'Weekly' : 'Monthly'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSiteVisitTooltipTable(propertyGroups.Etienne)}>{siteVisitsEtienne}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSiteVisitTooltipTable(propertyGroups.Bob)}>{siteVisitsBob}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSiteVisitTooltipTable(propertyGroups.Johan)}>{siteVisitsJohan}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSiteVisitTooltipTable(propertyGroups.Marina)}>{siteVisitsMarina}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSiteVisitTooltipTable(propertyGroups.Simphiwe)}>{siteVisitsSimphiwe}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSiteVisitTooltipTable(propertyGroups.Jean)}>{siteVisitsJean}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSiteVisitTooltipTable(allProperties)}>{siteVisitsTotal}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSiteVisitTooltipTable(allProperties, prevPeriodSiteVisitData)}>{prevPeriodSiteVisitsTotal}</td>
                            <td style={{
                                backgroundColor: siteVisitsVariance > 0 ? '#4CAF50' : siteVisitsVariance < 0 ? '#F63040' : '#808080',
                                color: 'white',
                                textAlign: 'center'
                            }}>
                                {siteVisitsVariance !== 0 ? siteVisitsVariance : '-'}
                            </td>
                        </tr>
                        <tr style={{ background: '#C0C0C0' }}>
                            <td style={{ textAlign: 'left', fontWeight: 'bold' }}>Short Posting - Service Providers</td>
                            <td>{viewMode === 'weekly' ? 'Weekly' : 'Monthly'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderShortPostingTooltipTable(propertyGroups.Etienne)}>{shortPostingsEtienne}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderShortPostingTooltipTable(propertyGroups.Bob)}>{shortPostingsBob}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderShortPostingTooltipTable(propertyGroups.Johan)}>{shortPostingsJohan}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderShortPostingTooltipTable(propertyGroups.Marina)}>{shortPostingsMarina}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderShortPostingTooltipTable(propertyGroups.Simphiwe)}>{shortPostingsSimphiwe}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderShortPostingTooltipTable(propertyGroups.Jean)}>{shortPostingsJean}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderShortPostingTooltipTable(allProperties)}>{shortPostingsTotal}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderShortPostingTooltipTable(allProperties, prevPeriodShortPostingsData)}>{prevPeriodShortPostingsTotal}</td>
                            <td style={{
                                backgroundColor: shortPostingsVariance > 0 ? '#F63040' : shortPostingsVariance < 0 ? '#4CAF50' : '#808080',
                                color: 'white',
                                textAlign: 'center'
                            }}>
                                {shortPostingsVariance !== 0 ? shortPostingsVariance : '-'}
                            </td>
                        </tr>
                        {/* Centre Rating */}
                        <tr>
                            <td style={{ background: '#57565B', color: '#fff', fontWeight: 'bold' }}>Centre Rating</td>
                            <td style={{ textAlign: 'left', fontWeight: 'bold' }}>SIPA score</td>
                            <td>Current</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSipaTooltipTable(propertyGroups.Etienne)}>{avgSipaEtienne !== null ? `${avgSipaEtienne}%` : '-'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSipaTooltipTable(propertyGroups.Bob)}>{avgSipaBob !== null ? `${avgSipaBob}%` : '-'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSipaTooltipTable(propertyGroups.Johan)}>{avgSipaJohan !== null ? `${avgSipaJohan}%` : '-'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSipaTooltipTable(propertyGroups.Marina)}>{avgSipaMarina !== null ? `${avgSipaMarina}%` : '-'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSipaTooltipTable(propertyGroups.Simphiwe)}>{avgSipaSimphiwe !== null ? `${avgSipaSimphiwe}%` : '-'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSipaTooltipTable(propertyGroups.Jean)}>{avgSipaJean !== null ? `${avgSipaJean}%` : '-'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderSipaTooltipTable(allProperties)}>{avgSipaTotal !== null ? `${avgSipaTotal}%` : '-'}</td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                        {/* Incidents */}
                        <tr style={{ background: '#E5E4E2' }}>
                            <td rowSpan="5" style={{ background: '#57565B', color: '#fff', fontWeight: 'bold', verticalAlign: 'middle' }}>Incidents</td>
                            <td style={{ textAlign: 'left', fontWeight: 'bold' }}>Armed robbery</td>
                            <td>{viewMode === 'weekly' ? 'Weekly' : 'Monthly'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('armedRobbery', propertyGroups.Etienne)}>{armedRobEtienne}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('armedRobbery', propertyGroups.Bob)}>{armedRobBob}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('armedRobbery', propertyGroups.Johan)}>{armedRobJohan}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('armedRobbery', propertyGroups.Marina)}>{armedRobMarina}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('armedRobbery', propertyGroups.Simphiwe)}>{armedRobSimphiwe}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('armedRobbery', propertyGroups.Jean)}>{armedRobJean}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('armedRobbery', allProperties)}>{armedRobTotal}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('armedRobbery', allProperties, prevPeriodIncidentsData)}>{prevPeriodArmedRob}</td>
                            <td style={{
                                backgroundColor: (armedRobTotal - prevPeriodArmedRob) > 0 ? '#F63040' : (armedRobTotal - prevPeriodArmedRob) < 0 ? '#4CAF50' : '#808080',
                                color: 'white', textAlign: 'center'
                            }}>{(armedRobTotal - prevPeriodArmedRob) !== 0 ? (armedRobTotal - prevPeriodArmedRob) : '-'}</td>
                        </tr>
                        <tr style={{ background: '#C0C0C0' }}>
                            <td style={{ textAlign: 'left' }}>Break-in/theft</td>
                            <td>{viewMode === 'weekly' ? 'Weekly' : 'Monthly'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('breakIn', propertyGroups.Etienne)}>{breakInEtienne}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('breakIn', propertyGroups.Bob)}>{breakInBob}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('breakIn', propertyGroups.Johan)}>{breakInJohan}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('breakIn', propertyGroups.Marina)}>{breakInMarina}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('breakIn', propertyGroups.Simphiwe)}>{breakInSimphiwe}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('breakIn', propertyGroups.Jean)}>{breakInJean}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('breakIn', allProperties)}>{breakInTotal}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('breakIn', allProperties, prevPeriodIncidentsData)}>{prevPeriodBreakIn}</td>
                            <td style={{
                                backgroundColor: (breakInTotal - prevPeriodBreakIn) > 0 ? '#F63040' : (breakInTotal - prevPeriodBreakIn) < 0 ? '#4CAF50' : '#808080',
                                color: 'white', textAlign: 'center'
                            }}>{(breakInTotal - prevPeriodBreakIn) !== 0 ? (breakInTotal - prevPeriodBreakIn) : '-'}</td>
                        </tr>
                        <tr>
                            <td style={{ textAlign: 'left' }}>Assault/injury</td>
                            <td>{viewMode === 'weekly' ? 'Weekly' : 'Monthly'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('assaultOrInjury', propertyGroups.Etienne)}>{assaultEtienne}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('assaultOrInjury', propertyGroups.Bob)}>{assaultBob}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('assaultOrInjury', propertyGroups.Johan)}>{assaultJohan}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('assaultOrInjury', propertyGroups.Marina)}>{assaultMarina}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('assaultOrInjury', propertyGroups.Simphiwe)}>{assaultSimphiwe}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('assaultOrInjury', propertyGroups.Jean)}>{assaultJean}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('assaultOrInjury', allProperties)}>{assaultTotal}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('assaultOrInjury', allProperties, prevPeriodIncidentsData)}>{prevPeriodAssault}</td>
                            <td style={{
                                backgroundColor: (assaultTotal - prevPeriodAssault) > 0 ? '#F63040' : (assaultTotal - prevPeriodAssault) < 0 ? '#4CAF50' : '#808080',
                                color: 'white', textAlign: 'center'
                            }}>{(assaultTotal - prevPeriodAssault) !== 0 ? (assaultTotal - prevPeriodAssault) : '-'}</td>
                        </tr>
                        <tr style={{ background: '#E5E4E2' }}>
                            <td style={{ textAlign: 'left' }}>Damage to property</td>
                            <td>{viewMode === 'weekly' ? 'Weekly' : 'Monthly'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('damageToProperty', propertyGroups.Etienne)}>{damageEtienne}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('damageToProperty', propertyGroups.Bob)}>{damageBob}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('damageToProperty', propertyGroups.Johan)}>{damageJohan}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('damageToProperty', propertyGroups.Marina)}>{damageMarina}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('damageToProperty', propertyGroups.Simphiwe)}>{damageSimphiwe}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('damageToProperty', propertyGroups.Jean)}>{damageJean}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('damageToProperty', allProperties)}>{damageTotal}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('damageToProperty', allProperties, prevPeriodIncidentsData)}>{prevPeriodDamage}</td>
                            <td style={{
                                backgroundColor: (damageTotal - prevPeriodDamage) > 0 ? '#F63040' : (damageTotal - prevPeriodDamage) < 0 ? '#4CAF50' : '#808080',
                                color: 'white', textAlign: 'center'
                            }}>{(damageTotal - prevPeriodDamage) !== 0 ? (damageTotal - prevPeriodDamage) : '-'}</td>
                        </tr>
                        <tr style={{ background: '#C0C0C0' }}>
                            <td style={{ textAlign: 'left' }}>Death on site</td>
                            <td>{viewMode === 'weekly' ? 'Weekly' : 'Monthly'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('deathOnSite', propertyGroups.Etienne)}>{getTotalIncident('deathOnSite', propertyGroups.Etienne)}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('deathOnSite', propertyGroups.Bob)}>{getTotalIncident('deathOnSite', propertyGroups.Bob)}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('deathOnSite', propertyGroups.Johan)}>{getTotalIncident('deathOnSite', propertyGroups.Johan)}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('deathOnSite', propertyGroups.Marina)}>{getTotalIncident('deathOnSite', propertyGroups.Marina)}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('deathOnSite', propertyGroups.Simphiwe)}>{getTotalIncident('deathOnSite', propertyGroups.Simphiwe)}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('deathOnSite', propertyGroups.Jean)}>{getTotalIncident('deathOnSite', propertyGroups.Jean)}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('deathOnSite', allProperties)}>{getTotalIncident('deathOnSite', allProperties)}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderIncidentTooltipTable('deathOnSite', allProperties, prevPeriodIncidentsData)}>{prevPeriodDeath}</td>
                            <td style={{
                                backgroundColor: (getTotalIncident('deathOnSite', allProperties) - prevPeriodDeath) > 0 ? '#F63040' : (getTotalIncident('deathOnSite', allProperties) - prevPeriodDeath) < 0 ? '#4CAF50' : '#808080',
                                color: 'white', textAlign: 'center'
                            }}>
                                {(getTotalIncident('deathOnSite', allProperties) - prevPeriodDeath) !== 0 ? (getTotalIncident('deathOnSite', allProperties) - prevPeriodDeath) : '-'}
                            </td>
                        </tr>
                        {/* Security */}
                        <tr>
                            <td style={{ background: '#57565B', color: '#fff', fontWeight: 'bold' }}>Security</td>
                            <td style={{ textAlign: 'left', fontWeight: 'bold' }}>Bloodhound score</td>
                            <td>{viewMode === 'weekly' ? 'Weekly' : 'Monthly'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderBloodhoundTooltipTable(propertyGroups.Etienne)}>
                                {avgBloodhoundEtienne !== null ? `${avgBloodhoundEtienne}%` : '-'}
                            </td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderBloodhoundTooltipTable(propertyGroups.Bob)}>
                                {avgBloodhoundBob !== null ? `${avgBloodhoundBob}%` : '-'}
                            </td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderBloodhoundTooltipTable(propertyGroups.Johan)}>
                                {avgBloodhoundJohan !== null ? `${avgBloodhoundJohan}%` : '-'}
                            </td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderBloodhoundTooltipTable(propertyGroups.Marina)}>
                                {avgBloodhoundMarina !== null ? `${avgBloodhoundMarina}%` : '-'}
                            </td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderBloodhoundTooltipTable(propertyGroups.Simphiwe)}>
                                {avgBloodhoundSimphiwe !== null ? `${avgBloodhoundSimphiwe}%` : '-'}
                            </td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderBloodhoundTooltipTable(propertyGroups.Jean)}>
                                {avgBloodhoundJean !== null ? `${avgBloodhoundJean}%` : '-'}
                            </td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderBloodhoundTooltipTable(allProperties)}>
                                {avgBloodhoundTotal !== null ? `${avgBloodhoundTotal}%` : '-'}
                            </td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderBloodhoundTooltipTable(allProperties, prevPeriodBloodhoundData)}>
                                {avgBloodhoundPrevPeriod !== null ? `${avgBloodhoundPrevPeriod}%` : '-'}
                            </td>
                            <td style={{
                                backgroundColor: bloodhoundVariance === null ? '#808080' : bloodhoundVariance > 0 ? '#4CAF50' : bloodhoundVariance < 0 ? '#F63040' : '#808080',
                                color: 'white',
                                textAlign: 'center'
                            }}>
                                {bloodhoundVariance !== null && bloodhoundVariance !== 0 ? `${bloodhoundVariance}%` : '-'}
                            </td>
                        </tr>
                        {/* OC */}
                        <tr style={{ background: '#E5E4E2' }}>
                            <td rowSpan="2" style={{ background: '#57565B', color: '#fff', fontWeight: 'bold', verticalAlign: 'middle' }}>OC</td>
                            <td style={{ textAlign: 'left', fontWeight: 'bold' }}>Fire outstanding</td>
                            <td>Current</td>
                            {['Etienne', 'Bob', 'Johan', 'Marina', 'Simphiwe', 'Jean'].map(groupName => (
                                <td
                                    key={groupName}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={renderOutstandingCertTooltip(propertyGroups[groupName], 'outstandingFireClearanceCertificateCount')}
                                >
                                    {outstandingFire[groupName] !== null ? outstandingFire[groupName] : '-'}
                                </td>
                            ))}
                            <td
                                data-tooltip-id="tt"
                                data-tooltip-content={renderOutstandingCertTooltip(allProperties, 'outstandingFireClearanceCertificateCount')}
                            >
                                {outstandingFire.Portfolio !== null ? outstandingFire.Portfolio : '-'}
                            </td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                        <tr style={{ background: '#C0C0C0' }}>
                            <td style={{ textAlign: 'left' }}>Occupancy certificate</td>
                            <td>Current</td>
                            {['Etienne', 'Bob', 'Johan', 'Marina', 'Simphiwe', 'Jean'].map(groupName => (
                                <td
                                    key={groupName}
                                    data-tooltip-id="tt"
                                    data-tooltip-content={renderOutstandingCertTooltip(propertyGroups[groupName], 'outstandingOccupancyCertificateCount')}
                                >
                                    {outstandingOccupancy[groupName] !== null ? outstandingOccupancy[groupName] : '-'}
                                </td>
                            ))}
                            <td
                                data-tooltip-id="tt"
                                data-tooltip-content={renderOutstandingCertTooltip(allProperties, 'outstandingOccupancyCertificateCount')}
                            >
                                {outstandingOccupancy.Portfolio !== null ? outstandingOccupancy.Portfolio : '-'}
                            </td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                        {/* Assets */}
                        <tr>
                            <td rowSpan="4" style={{ background: '#57565B', color: '#fff', fontWeight: 'bold', verticalAlign: 'middle' }}>Assets</td>
                            <td style={{ textAlign: 'left', fontWeight: 'bold' }}>Services Due</td>
                            <td>Current</td>
                            {['Etienne', 'Bob', 'Johan', 'Marina', 'Simphiwe', 'Jean'].map(groupName => (
                                <td key={groupName} data-tooltip-id="tt" data-tooltip-content={renderAssetTooltipTable(groupName, 'due')}>
                                    {groupAssetStatus[groupName]?.due ?? 0}
                                </td>
                            ))}
                            <td data-tooltip-id="tt" data-tooltip-content={renderAssetTooltipTable(null, 'due')}>{portfolioDue}</td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                        <tr style={{ background: '#E5E4E2' }}>
                            <td style={{ textAlign: 'left' }}>Services Overdue</td>
                            <td>Current</td>
                            {['Etienne', 'Bob', 'Johan', 'Marina', 'Simphiwe', 'Jean'].map(groupName => (
                                <td key={groupName} data-tooltip-id="tt" data-tooltip-content={renderAssetTooltipTable(groupName, 'overdue')}>
                                    {groupAssetStatus[groupName]?.overdue ?? 0}
                                </td>
                            ))}
                            <td data-tooltip-id="tt" data-tooltip-content={renderAssetTooltipTable(null, 'overdue')}>{portfolioOverdue}</td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                        <tr style={{ background: '#C0C0C0' }}>
                            <td style={{ textAlign: 'left' }}>Under Maintenance</td>
                            <td>Current</td>
                            {['Etienne', 'Bob', 'Johan', 'Marina', 'Simphiwe', 'Jean'].map(groupName => (
                                <td key={groupName} data-tooltip-id="tt" data-tooltip-content={renderAssetTooltipTable(groupName, 'underMaintenance')}>
                                    {groupAssetStatus[groupName]?.underMaintenance ?? 0}
                                </td>
                            ))}
                            <td data-tooltip-id="tt" data-tooltip-content={renderAssetTooltipTable(null, 'underMaintenance')}>{portfolioUnderMaintenance}</td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                        <tr>
                            <td style={{ textAlign: 'left' }}>Pending Verification</td>
                            <td>Current</td>
                            {['Etienne', 'Bob', 'Johan', 'Marina', 'Simphiwe', 'Jean'].map(groupName => (
                                <td key={groupName} data-tooltip-id="tt" data-tooltip-content={renderAssetTooltipTable(groupName, 'pendingVerification')}>
                                    {groupAssetStatus[groupName]?.pendingVerification ?? 0}
                                </td>
                            ))}
                            <td data-tooltip-id="tt" data-tooltip-content={renderAssetTooltipTable(null, 'pendingVerification')}>{portfolioPendingVerification}</td>
                            <td>-</td>
                            <td>-</td>
                        </tr>
                        {/* IT */}
                        <tr style={{ background: '#E5E4E2' }}>
                            <td style={{ background: '#57565B', color: '#fff', fontWeight: 'bold' }}>IT</td>
                            <td style={{ textAlign: 'left', fontWeight: 'bold' }}>Network downtime hours</td>
                            <td>Hours</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderNetworkDowntimeTooltip(networkDowntimeData, propertyGroupIds.Etienne)}>{downtimeEtienne}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderNetworkDowntimeTooltip(networkDowntimeData, propertyGroupIds.Bob)}>{downtimeBob}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderNetworkDowntimeTooltip(networkDowntimeData, propertyGroupIds.Johan)}>{downtimeJohan}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderNetworkDowntimeTooltip(networkDowntimeData, propertyGroupIds.Marina)}>{downtimeMarina}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderNetworkDowntimeTooltip(networkDowntimeData, propertyGroupIds.Simphiwe)}>{downtimeSimphiwe}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderNetworkDowntimeTooltip(networkDowntimeData, propertyGroupIds.Jean)}>{downtimeJean}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderNetworkDowntimeTooltip(networkDowntimeData, allPropertyIds)}>{downtimePortfolio}</td>
                            <td>{downtimePrevWeek}</td>
                            <td style={{
                                backgroundColor: downtimeVariance > 0 ? '#F63040' : downtimeVariance < 0 ? '#4CAF50' : '#808080',
                                color: 'white', textAlign: 'center'
                            }}>
                                {downtimeVariance !== 0 ? downtimeVariance.toFixed(2) : '-'}
                            </td>
                        </tr>
                        {/* Checklist */}
                        <tr style={{ background: '#C0C0C0' }}>
                            <td style={{ background: '#57565B', color: '#fff', fontWeight: 'bold' }}>Checklists</td>
                            <td style={{ textAlign: 'left', fontWeight: 'bold' }}>Daily checklist submission failure</td>
                            <td>{viewMode === 'weekly' ? 'Weekly' : 'Monthly'}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderChecklistTooltipTable(propertyGroups.Etienne)}>{checklistsEtienne}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderChecklistTooltipTable(propertyGroups.Bob)}>{checklistsBob}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderChecklistTooltipTable(propertyGroups.Johan)}>{checklistsJohan}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderChecklistTooltipTable(propertyGroups.Marina)}>{checklistsMarina}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderChecklistTooltipTable(propertyGroups.Simphiwe)}>{checklistsSimphiwe}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderChecklistTooltipTable(propertyGroups.Jean)}>{checklistsJean}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderChecklistTooltipTable(allProperties)}>{checklistsTotal}</td>
                            <td data-tooltip-id="tt" data-tooltip-content={renderChecklistTooltipTable(allProperties, prevPeriodChecklistData)}>{prevChecklistsTotal}</td>
                            <td style={{
                                backgroundColor: varianceTotal > 0 ? '#F63040' : varianceTotal < 0 ? '#4CAF50' : '#808080',
                                color: 'white',
                                textAlign: 'center'
                            }}>
                                {varianceTotal !== 0 ? varianceTotal : '-'}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <ReactTooltip
                id="tt"
                place="bottom"
                variant="dark"
                delayShow={150}
                delayHide={120}
                isOpen={tooltipOpen}
                setIsOpen={setTooltipOpen}
                openEvents={{ mouseenter: true }}
                closeEvents={{ mouseleave: true }}
                globalCloseEvents={{ escape: true, scroll: true, resize: true, clickOutsideAnchor: true }}
                style={{ maxWidth: '350px', padding: '6px', pointerEvents: 'none' }}
                render={({ content }) =>
                    content ? <div dangerouslySetInnerHTML={{ __html: content }} /> : null
                }
            />
        </div>
    );

    // #endregion
}

export default RegionalPortfolioStats